import { useAuthorization, useAxios, useMsal } from "@sttedra_ui/framework";
import axios from "axios";
import { usersGetDetails } from "@/api/users/users.api.js";

const { modelRef: authorizationModelRef } = useAuthorization();
const { base } = useAxios();

// Check if the user is connected to the MITMA network
async function isInMitmaNetwork() {
  try {
    // Make the request
    const response = await axios.get(
      "https://aplicacionesinternas.fomento.es/ping/",
    );

    return response.status == 200;
  } catch (error) {
    console.error("Error checking if the user is in the MITMA network", error);

    return false;
  }
}

function parseJwt(token) {
  const base64Url = token.split(".")[1];
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split("")
      .map(function (c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join(""),
  );

  return JSON.parse(jsonPayload);
}

async function setCustomAccessToken(token, { checkToken = true } = {}) {
  localStorage.setItem("accessToken", token);

  base.interceptors.request.use(
    async function (config) {
      config.headers.set("AuthorizationVisualizador", token);

      if (checkToken) {
        checkTokenExpired();
      }

      return config;
    },
    function (error) {
      return Promise.reject(error);
    },
  );

  base.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error.response?.status === 401) {
        await clearAuthData();
      }

      return Promise.reject(error);
    },
  );

  const tokenData = parseJwt(token);
  const response = await usersGetDetails(tokenData.email);

  if (response.status == 200) {
    const user = response.data;

    authorizationModelRef.value = {
      roles: [user.rol],
      email: user.email,
      user,
    };
  }
}

async function getMsalToken() {
  return await useMsal().acquireToken();
}

async function clearAuthData({ manualLogout = false } = {}) {
  localStorage.removeItem("accessToken");

  if (manualLogout === true) {
    localStorage.setItem("manualLogout", "true");
    // No eliminamos lastRoute para permitir redirección a la URL original después del login
    localStorage.removeItem("impersonationToken");
  } else {
    localStorage.removeItem("manualLogout");
  }

  authorizationModelRef.value = {
    roles: [],
    email: null,
    user: null,
  };

  await useMsal().logOut();
}

async function checkTokenExpired({ expectedResponse = true } = {}) {
  const accessToken = localStorage.getItem("accessToken");
  const expired = accessToken ? isTokenExpired(accessToken) : false;

  if (expired) {
    await clearAuthData();
  }

  if (expectedResponse) {
    return expired;
  }
}

function isTokenExpired(token) {
  const defaultTimeExpiration = 35; // minutos
  const defaultMaximumTimeExpiration = 480; // 8 horas

  let expirationTime = null;
  let maximumExpirationTime = null;

  try {
    expirationTime = STTEDRA_TOKEN_EXPIRATION_TIME;

    if (
      expirationTime == null ||
      expirationTime == "null" ||
      isNaN(expirationTime)
    ) {
      expirationTime = defaultTimeExpiration;
    } else {
      expirationTime = parseFloat(expirationTime);
    }
  } catch (error) {
    expirationTime = defaultTimeExpiration;
  }

  try {
    maximumExpirationTime = STTEDRA_TOKEN_MAXIMUM_EXPIRATION_TIME;

    if (
      maximumExpirationTime == null ||
      maximumExpirationTime == "null" ||
      isNaN(maximumExpirationTime)
    ) {
      maximumExpirationTime = defaultMaximumTimeExpiration;
    } else {
      maximumExpirationTime = parseFloat(maximumExpirationTime);
    }
  } catch (error) {
    maximumExpirationTime = defaultMaximumTimeExpiration;
  }

  // Para testing
  // expirationTime = 0.2;
  // maximumExpirationTime = 0.2;

  token = parseJwt(token);

  const now = Math.floor(Date.now() / 1000); // segundos actuales

  const exp = token.exp; // expiración
  const iat = token.iat; // issued at

  // 1. ¿Pasaron más de [maximumExpirationTime] minutos desde iat?
  const maximumSeconds = maximumExpirationTime * 60; // en segundos

  if (iat && now >= iat + maximumSeconds) {
    // Excedió el tiempo máximo (por ejemplo 8 horas). Limpiar datos de auth.
    clearAuthData({ manualLogout: true });
    localStorage.setItem("maximumExpirationTime", "true");
    return true;
  }

  // 2. ¿Pasaron más de [expirationTime] minutos desde iat?
  const seconds = expirationTime * 60; // en segundos

  if (iat && now >= iat + seconds) {
    return true; // ya pasaron [expirationTime] minutos desde que se emitió
  }

  // 3. ¿Está expirado?
  if (exp && now >= exp) {
    return true; // ya expiró
  }

  return false; // válido
}

function setImpersonationToken(user) {
  base.interceptors.request.use(
    function (config) {
      config.headers.set("impersonateuserid", user.email);

      return config;
    },
    function (error) {
      return Promise.reject(error);
    },
  );
}

function impersonatedUserIsAdmin() {
  const impersonationToken = localStorage.getItem("impersonationToken");

  if (impersonationToken) {
    const user = JSON.parse(impersonationToken);
    return user.esAdministrador;
  }

  return null;
}

function isLogged() {
  const accessToken = localStorage.getItem("accessToken");
  return accessToken != null;
}

export {
  isInMitmaNetwork,
  setCustomAccessToken,
  getMsalToken,
  clearAuthData,
  checkTokenExpired,
  setImpersonationToken,
  impersonatedUserIsAdmin,
  isLogged,
};
